import React from "react";
import { motion } from "framer-motion";
import { 
  FaGavel,
  FaUserCheck,
  FaUserCircle,
  FaTrophy,
  FaMoneyBillWave,
  FaShieldAlt,
  FaLock,
  FaExclamationTriangle,
  FaSyncAlt,
  FaMapMarkerAlt,
  FaEnvelope
} from "react-icons/fa";
import BackButton from "./BackButton";
import "./Terms.css";

const Terms = () => {
  return (
    <div className="terms-container dark-theme">
      <nav className="terms-nav">
        <BackButton />
      </nav>

      <motion.div 
        className="terms-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <FaGavel className="header-icon" />
        <h1 className="terms-title">Terms and Conditions</h1>
        <p className="terms-subtitle">Last Updated: {new Date().toLocaleDateString()}</p>
      </motion.div>

      <div className="terms-content">
        <motion.div 
          className="terms-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="card-header">
            <FaGavel className="card-icon" />
            <h2>Acceptance of Terms</h2>
          </div>
          <div className="card-content">
            <p>
              By using the <span className="highlight">AUBattle</span> app, you agree to comply with 
              these Terms and Conditions. If you disagree, you must not use the app.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaUserCheck className="card-icon" />
            <h2>Eligibility</h2>
          </div>
          <div className="card-content">
            <p>
              You must be at least <span className="highlight">18 years old</span> or the age of majority in your 
              jurisdiction to participate in tournaments.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaUserCircle className="card-icon" />
            <h2>Account Registration</h2>
          </div>
          <div className="card-content">
            <p>
              You must provide <span className="highlight">accurate and complete</span> information during 
              registration and keep it updated.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaTrophy className="card-icon" />
            <h2>Tournament Participation</h2>
          </div>
          <div className="card-content">
            <p>
              Participation requires payment of an <span className="highlight">entry fee</span>, with rules 
              clearly stated before entry.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaMoneyBillWave className="card-icon" />
            <h2>Payments and Prizes</h2>
          </div>
          <div className="card-content">
            <ul className="terms-list">
              <li>
                <span className="term-label">Entry Fees:</span>
                <span>Non-refundable unless otherwise stated</span>
              </li>
              <li>
                <span className="term-label">Prizes:</span>
                <span>Distributed as specified in tournament details</span>
              </li>
              <li>
                <span className="term-label">Disqualification:</span>
                <span>For any rule violations</span>
              </li>
            </ul>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaShieldAlt className="card-icon" />
            <h2>Conduct and Fair Play</h2>
          </div>
          <div className="card-content">
            <p>
              <span className="highlight">Cheating, hacking, or unauthorized software</span> is strictly 
              prohibited and will result in immediate disqualification.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaLock className="card-icon" />
            <h2>Privacy Policy</h2>
          </div>
          <div className="card-content">
            <p>
              Your use is also governed by our <span className="highlight">Privacy Policy</span>, which describes 
              how we handle your personal information.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaExclamationTriangle className="card-icon" />
            <h2>Limitation of Liability</h2>
          </div>
          <div className="card-content">
            <p>
              AUBattle is not responsible for any <span className="highlight">damages or losses</span> arising 
              from app use or tournament participation.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaSyncAlt className="card-icon" />
            <h2>Changes to Terms</h2>
          </div>
          <div className="card-content">
            <p>
              AUBattle may <span className="highlight">update these Terms</span> periodically. Changes will be 
              posted within the app.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaMapMarkerAlt className="card-icon" />
            <h2>Governing Law</h2>
          </div>
          <div className="card-content">
            <p>
              These Terms are governed by the laws of the jurisdiction where 
              <span className="highlight"> AUBattle operates</span>.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="terms-card"
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="card-header">
            <FaEnvelope className="card-icon" />
            <h2>Contact Information</h2>
          </div>
          <div className="card-content">
            <p>
              For questions, contact AUBattle support at 
              <span className="highlight"> support@aubattle.com</span>.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;